import React from "react"
import { SubscriptionOperationHandler } from "@root/components/preference-center/types"
import { useAppDispatch } from "@root/store"
import {
  selectChannelSubscriptions,
  selectContactSubscriptions,
} from "@root/store/selectors"
import {
  subscribeChannel,
  subscribeContact,
  unsubscribeChannel,
  unsubscribeContact,
} from "@root/store/thunks"
import {
  channelToSubscriptionList,
  contactToSubscriptionList,
  isCrossChannel,
} from "@root/store/utils"
import {
  PreferenceCenter as PreferenceCenterType,
  Scope,
} from "@ua/web-sdk/lib/remote-data/preference-center"
import { useSelector } from "react-redux"
import PreferenceCenter from "@root/components/preference-center"
import { SubscriptionList } from "@root/store/types"

export type Props = {
  definition: PreferenceCenterType
}

const ConnectedPreferenceCenter: React.FC<Props> = ({ definition }) => {
  const dispatch = useAppDispatch()
  const channelSubscriptions = useSelector(selectChannelSubscriptions)
  const contactSubscriptions = useSelector(selectContactSubscriptions)
  const channelSubscribe = React.useCallback(
    (listId: string) => dispatch(subscribeChannel(listId)),
    [dispatch]
  )
  const channelUnsubscribe = React.useCallback(
    (listId: string) => dispatch(unsubscribeChannel(listId)),
    [dispatch]
  )
  const contactSubscribe = React.useCallback(
    (listId: string, scope: Scope) =>
      dispatch(subscribeContact({ listId, scope })),
    [dispatch]
  )
  const contactUnsubscribe = React.useCallback(
    (listId: string, scope: Scope) =>
      dispatch(unsubscribeContact({ listId, scope })),
    [dispatch]
  )

  const subscriptionOperation = React.useCallback<SubscriptionOperationHandler>(
    (operation, listId, scope) => {
      switch (operation) {
        case "subscribe":
          scope ? contactSubscribe(listId, scope) : channelSubscribe(listId)
          return
        case "unsubscribe":
          scope ? contactUnsubscribe(listId, scope) : channelUnsubscribe(listId)
          return
        default:
          const msg: never = operation
          throw new Error(`invalid operation: ${msg}`)
      }
    },
    [contactSubscribe, channelSubscribe, contactUnsubscribe, channelUnsubscribe]
  )

  const subscriptions = React.useMemo<SubscriptionList[]>(
    () =>
      isCrossChannel(definition)
        ? contactToSubscriptionList(contactSubscriptions)
        : channelToSubscriptionList(channelSubscriptions),
    [definition, contactSubscriptions, channelSubscriptions]
  )

  return (
    <PreferenceCenter
      definition={definition}
      subscriptions={subscriptions}
      onSubscriptionOperation={subscriptionOperation}
    />
  )
}

export default ConnectedPreferenceCenter
